import React from 'react'
import {
  Box,
  ButtonBase,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { viz } from '../../../userPages/dashboard/managerOverview/overviewTheme'

const ringColor = (percent) => {
  if (percent === null || percent === undefined) return viz.deEmphasis
  if (percent >= 95) return viz.good
  if (percent >= 80) return viz.warning
  return viz.critical
}

const ComplianceRing = ({ label, caption, percent, url }) => {
  const navigate = useNavigate()
  const hasValue = percent !== null && percent !== undefined
  const rounded = hasValue ? Math.round(percent) : null
  const color = ringColor(rounded)

  return (
    <ButtonBase
      onClick={() => url && navigate(url)}
      sx={{
        flex: '1 0 130px',
        minWidth: 130,
        borderRadius: 1,
        p: 1.5,
        flexDirection: 'column',
        gap: 1,
        '&:hover': { backgroundColor: 'anMuted.main' },
      }}
    >
      <Box position="relative" display="inline-flex">
        <CircularProgress
          variant="determinate"
          value={100}
          size={78}
          thickness={4}
          sx={{ color: 'greycontrast.main', position: 'absolute' }}
        />
        <CircularProgress
          variant="determinate"
          value={Math.min(rounded ?? 0, 100)}
          size={78}
          thickness={4}
          sx={{ color }}
        />
        <Box
          sx={{
            inset: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography fontWeight={700} sx={{ color, fontSize: '1rem' }}>
            {hasValue ? `${rounded}%` : '—'}
          </Typography>
        </Box>
      </Box>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1.2 }}
      >
        {caption}
      </Typography>
    </ButtonBase>
  )
}

// Headline coverage across the three workforce compliance measures
const CompliancePulse = ({ summary, training }) => {
  const loading = !summary

  const trainingPercent = training ? parseFloat(training.percent) : null
  const trackedCourses = (training?.complianceCounts ?? []).filter(
    (course) => course.totalRequiredEmployees > 0,
  ).length

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack>
        <Typography variant="title">Compliance pulse</Typography>
        <Typography variant="smallGrey">
          Live coverage across the active workforce
        </Typography>
      </Stack>
      {loading ? (
        <Stack direction="row" gap={1} mt={1.5}>
          {[...Array(3)].map((_, i) => (
            <Stack
              key={i}
              alignItems="center"
              gap={1}
              sx={{ flex: '1 0 130px', p: 1.5 }}
            >
              <Skeleton variant="circular" width={78} height={78} />
              <Skeleton variant="text" width="70%" />
            </Stack>
          ))}
        </Stack>
      ) : (
        <Stack
          direction="row"
          gap={1}
          mt={1}
          sx={{ overflowX: 'auto', pb: 0.5 }}
        >
          <ComplianceRing
            label="Mandatory training"
            caption={
              training
                ? `Across ${trackedCourses} tracked course${
                    trackedCourses === 1 ? '' : 's'
                  }`
                : 'Unavailable right now'
            }
            percent={trainingPercent}
            url="/employees/mandatory-training"
          />
          <ComplianceRing
            label="DBS checks"
            caption={
              (summary.expiredDBS ?? 0) > 0
                ? `${summary.expiredDBS} staff need a new check`
                : 'All staff in date'
            }
            percent={summary.dbsCompliance}
            url="/dbs-checks"
          />
          <ComplianceRing
            label="Policy acknowledgement"
            caption="Average across latest policy versions"
            percent={summary.policyAcknowledgement}
            url="/policy-library"
          />
          {summary.occupationalHealthCompliance !== undefined && (
            <ComplianceRing
              label="Occupational health"
              caption={
                (summary.occupationalHealthOutstanding ?? 0) > 0
                  ? `${summary.occupationalHealthOutstanding} staff need a screening`
                  : 'All staff in date'
              }
              percent={summary.occupationalHealthCompliance}
              url="/occupational-health"
            />
          )}
        </Stack>
      )}
    </Paper>
  )
}

export default CompliancePulse
